<script lang="ts">
import {computed, defineComponent, onMounted, ref} from "vue";
import MasterLayout from "~/layouts/MasterLayout.vue";
import {usePageStore} from "~/plugins/store/page";
import {
  type Contact,
  type ContactForm,
  createDefaultContact,
  createDefaultContactForm,
  createDefaultValidationContactFormStatus, type ValidationContactFormStatus
} from "~/types/Page";
import {translation, useTranslationStore} from "~/plugins/store/translations";
import type {TranslationList} from "~/types/Utils";
import LoadingDialog from "~/components/utils/LoadingDialog.vue";
import {useAuthStore} from "~/plugins/store/auth";
import {useReCaptcha} from "vue-recaptcha-v3";
import {useNuxtApp} from "#app";
import {recaptcha} from "~/composables/recaptcha";
import {reloadNuxtApp} from "nuxt/app";

export default defineComponent({
  components: {LoadingDialog, MasterLayout},
  setup() {
    useHead({
      title: 'Xiga | Contactează-ne',
    });
    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
    const pageStore = usePageStore();
    const contactData = ref<Contact>(createDefaultContact());
    const showLoadingDialog = ref(false);
    const messageLoadingDialog = ref('');
    const translationStore = useTranslationStore();
    const translationList = ref<TranslationList>({}); // Initialize translations as a reactive reference
    const contactForm = ref<ContactForm>(createDefaultContactForm());
    const validationContactFormStatus = ref<ValidationContactFormStatus>(createDefaultValidationContactFormStatus());
    const authStore = useAuthStore();
    const accessTokens = authStore.getAccessToken();
    const recaptchaInstance = useReCaptcha();
    const user = computed(() => authStore.getUser());
    const {$toast} = useNuxtApp();

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
      showLoadingDialog.value = true;
      messageLoadingDialog.value = translation(translationList.value.loading, 'Se incarcă...');

      const tmpContactData = await pageStore.getContactData();
      if (tmpContactData) {
        contactData.value = tmpContactData;
      }
      if (user.value) {
        contactForm.value.email = user.value.email;
        contactForm.value.phone_number = user.value.phone_number;
        contactForm.value.first_name = user.value.first_name;
        contactForm.value.last_name = user.value.last_name;
      }
      showLoadingDialog.value = false;
    });

    const handleValidationContactFormStatus = ({field, isValid}: { field: string, isValid: boolean }) => {
      validationContactFormStatus.value[field] = isValid;
    };

    const submitContactForm = async () => {
      showLoadingDialog.value = true;
      messageLoadingDialog.value = translation(translationList.value.loading, 'Se incarcă...');

      const contactFields = [
        'is_customer_legal',
        'email',
        'phone_number',
        'text',
      ];

      if (contactForm.value.is_customer_legal) {
        contactFields.push('cif_code');
        contactFields.push('company_name');
      } else {
        contactFields.push('first_name');
        contactFields.push('last_name');
      }

      const isCustomerFormValid = (() => {
        let allValid = true;
        contactFields.forEach(field => {
          if (!validationContactFormStatus.value[field]) {
            console.log('Validation failed for field: ', field);
            window.dispatchEvent(new CustomEvent('validate-customer', {detail: field}));
            allValid = false;
          }
        });
        return allValid;
      })();

      if (!isCustomerFormValid || !accessTokens) {
        $toast.error('Formularul de contact conține erori. Vă rugăm să corectați erorile și să trimiteți din nou.');
        showLoadingDialog.value = false;
        return;
      }

      const token = await recaptcha(recaptchaInstance);
      pageStore.submitContactForm(contactForm.value, accessTokens, token)
          .then(async (response) => {
            console.log(response);
            if (response) {
              $toast.success('Formularul de contact a fost trimis cu succes.');
              await delay(2000);
              reloadNuxtApp();
            } else {
              $toast.error('Formularul de contact nu a putut fi trimis. Vă rugăm să încercați din nou.');
            }
          })
          .catch((error) => {
            $toast.error('Formularul de contact nu a putut fi trimis. Vă rugăm să încercați din nou.');
          })
          .finally(() => {
            showLoadingDialog.value = false;
          });
    };

    return {
      translationList,
      translation,
      showLoadingDialog,
      messageLoadingDialog,
      contactData,
      contactForm,
      handleValidationContactFormStatus,
      submitContactForm,
      user
    }
  }
});
</script>

<template>
  <master-layout class="static">
    <v-container class="content-container">
      <v-container fluid class="component-container-1400mw">
        <v-container class="background-default" style="max-width: 1200px; border-radius: 15px;">
          <loading-dialog :message="messageLoadingDialog" :show-dialog="showLoadingDialog"/>
          <!-- Content -->
          <v-card class="background-default" min-height="500" v-if="contactData && contactData.company" flat
                  rounded="lg">
            <v-card-title>
              <h1 class="text-center">Contact</h1>
            </v-card-title>

            <v-divider></v-divider>

            <v-row class="mt-3">
              <v-col cols="12" sm="12" md="6" lg="6">
                <v-row>
                  <v-col cols="12">
                    <customer-type-tab
                        v-model="contactForm.is_customer_legal"/>
                  </v-col>
                  <div v-if="contactForm.is_customer_legal" class="w-100">
                    <v-col cols="12">
                      <customer-cif-code
                          v-model="contactForm.cif_code"
                          @validation-customer-status="handleValidationContactFormStatus"/>
                    </v-col>
                    <v-col cols="12">
                      <customer-company-name
                          v-model="contactForm.company_name"
                          @validation-customer-status="handleValidationContactFormStatus"
                      />
                    </v-col>
                  </div>
                  <div v-else class="w-100">
                    <v-col cols="12">
                      <customer-last-name
                          v-model="contactForm.last_name"
                          @validation-customer-status="handleValidationContactFormStatus"/>
                    </v-col>
                    <v-col cols="12">
                      <customer-first-name
                          v-model="contactForm.first_name"
                          @validation-customer-status="handleValidationContactFormStatus"
                      />
                    </v-col>
                  </div>
                  <v-col cols="12">
                    <customer-email
                        :is-readonly="user ? true : false"
                        v-model="contactForm.email"
                        @validation-customer-status="handleValidationContactFormStatus"/>
                  </v-col>
                  <v-col cols="12">
                    <customer-phone-number
                        v-model="contactForm.phone_number"
                        @validation-customer-status="handleValidationContactFormStatus"/>
                  </v-col>
                  <v-col cols="12">
                    <customer-text
                        v-model="contactForm.text"
                        @validation-customer-status="handleValidationContactFormStatus"/>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" class="d-flex align-center justify-center">
                    <v-btn
                        class="block-on-mobile"
                        color="primary"
                        size="x-large"
                        flat
                        rounded="lg"
                        @click="submitContactForm"
                    >{{ translation(translationList.submit, 'Trimite') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" sm="12" md="6" lg="6" class="text-center">
                <div>
                  <h3 class="font-weight-bold">
                    <v-icon size="x-large" icon="mdi-office-building-marker"></v-icon>
                    {{ contactData.company.company_name }}
                  </h3>
                  <div class="my-2">
                    <p v-if="contactData.company.company_address" class="font-weight-regular">
                      <strong>Adresa: </strong>{{ contactData.company.company_address }}
                    </p>
                    <p v-if="contactData.company.company_website" class="font-weight-regular">
                      <strong>Website: </strong>
                      <NuxtLink :to="contactData.company.company_website" rel="noopener" target="_blank">
                        {{ contactData.company.company_website }}
                      </NuxtLink>
                    </p>
                    <p v-if="contactData.company.company_email" class="font-weight-regular">
                      <strong>Email: </strong>
                      <NuxtLink :to="'mailto:' + contactData.company.company_email">{{
                          contactData.company.company_email
                        }}
                      </NuxtLink>
                    </p>
                  </div>
                  <h3 class="font-weight-bold">
                    <v-icon size="x-large" icon="mdi-badge-account"></v-icon>
                    {{ contactData.contact_first_name }} {{ contactData.contact_last_name }}
                  </h3>
                  <div class="my-2">
                    <p v-if="contactData.contact_phone_1" class="font-weight-regular">
                      <strong>Telefon: </strong>{{ contactData.contact_phone_1 }}
                    </p>
                    <p v-if="contactData.contact_phone_2" class="font-weight-regular">
                      <strong>Telefon Secundar: </strong>{{ contactData.contact_phone_2 }}
                    </p>
                    <p v-if="contactData.contact_email" class="font-weight-regular">
                      <strong>Email: </strong>
                      <NuxtLink :to="'mailto:' + contactData.contact_email">{{ contactData.contact_email }}</NuxtLink>
                    </p>
                    <p v-if="contactData.contact_skype" class="font-weight-regular">
                      <strong>Skype: </strong>{{ contactData.contact_skype }}
                    </p>
                    <p v-if="contactData.contact_address" class="font-weight-regular">
                      <strong>Adresa: </strong>{{ contactData.contact_address }}
                    </p>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card>

          <!-- Undefined page -->
          <v-card v-else-if="(!contactData || !contactData.company) && !showLoadingDialog"
                  class="d-flex flex-column justify-center align-center" height="500px" color="transparent" flat>
            <div class="fw-500 fs-50">
              Pagină nedefinită
            </div>
          </v-card>
        </v-container>
      </v-container>
    </v-container>
  </master-layout>
</template>


<style scoped>

</style>
